import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { 
  Button, 
  Spinner, 
  Card, 
  CardBody, 
  Chip, 
  Image, 
  Tabs, 
  Tab, 
  Divider, 
  Tooltip, 
  Snippet,
  ScrollShadow,
  CardHeader,
} from "@heroui/react";
import { 
  CheckCircle2, 
  AlertCircle, 
  Home, 
  ArrowLeft, 
  Printer, 
  Share2, 
  Activity, 
  Scale, 
  Dna, 
  Info,
  CalendarDays,
  MapPin,
} from "lucide-react";
import AdvancedGeneticTool from "@/components/template";
import { apiClient } from "@/api/api.ts";

// Helper for formatting dates safely
const formatDate = (dateString: string | Date) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-GB", {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Helper for calculating age (rough estimate if birthDate is string)
const calculateAge = (birthDate: string) => {
  if (!birthDate) return "N/A";
  const birth = new Date(birthDate);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - birth.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  if (diffDays < 30) return `${diffDays} days`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months`;
  return `${Math.floor(diffDays / 365)} years`;
};

const AnimalVerificationPage = () => {
  const { id } = useParams<{ id: string }>();
  const [animal, setAnimal] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("identity");

  // Interaction Handlers
  const handlePrint = () => {
    window.print();
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Animal Verification: ${animal?.name || 'Unknown'}`,
          text: `Verify genetic data for animal ID: ${animal?.animalID}`,
          url: window.location.href,
        });
      } catch (err) {
        console.log("Share canceled");
      }
    } else {
      // Fallback: Copy URL
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  useEffect(() => {
    const fetchAnimal = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get(`/animals/animal/${id}`);
        setAnimal(response.data);
      } catch (err) {
        console.error("Verification Error:", err);
        setError("This animal registration could not be verified in our records.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchAnimal();
  }, [id]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-6 bg-default-50">
        <div className="relative">
            <div className="absolute inset-0 animate-ping rounded-full bg-primary/20"></div>
            <Spinner size="lg" color="primary" classNames={{ wrapper: "w-16 h-16" }} />
        </div>
        <div className="flex flex-col items-center gap-2">
            <h2 className="text-xl font-semibold text-default-700">Accessing Sila Genetics Database</h2>
            <p className="text-default-500 text-sm animate-pulse">Verifying cryptographic signature...</p>
        </div>
      </div>
    );
  }

  if (error || !animal) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-default-50">
        <Card className="max-w-md w-full shadow-xl border-danger/20 border-t-4">
          <CardBody className="flex flex-col items-center p-8 gap-6 text-center">
            <div className="p-6 rounded-full bg-danger/10 text-danger mb-2 shadow-inner">
              <AlertCircle size={64} strokeWidth={1.5} />
            </div>
            <div>
                <h1 className="text-3xl font-bold text-default-900">Verification Failed</h1>
                <p className="text-default-500 mt-2">{error}</p>
            </div>
            <Divider className="my-2"/>
            <div className="flex flex-col w-full gap-3">
              <Button as={Link} to="/" variant="solid" color="danger" startContent={<Home size={18}/>}>
                Return to Dashboard
              </Button>
              <Button variant="ghost" onPress={() => window.location.reload()}>Try Again</Button>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  // Determine status color
  const statusColor = animal.approvalStatus === "Approved" ? "success" : animal.approvalStatus === "Rejected" ? "danger" : "warning";
  const statusIcon = animal.approvalStatus === "Approved" ? <CheckCircle2 size={16}/> : <Activity size={16}/>;

  return (
    <div className="min-h-screen bg-default-100/50 pb-12 print:bg-white print:p-0">
      
      {/* Sticky Top Navigation / Verification Bar */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-default-200 shadow-sm print:hidden">
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-4">
          
          <div className="flex items-center gap-4">
            <Button as={Link} to="/" isIconOnly variant="light" radius="full" aria-label="Go Back">
                <ArrowLeft size={20} className="text-default-600" />
            </Button>
            <div className="flex flex-col">
                <div className="flex items-center gap-2">
                    <h1 className="text-lg font-bold text-default-900 leading-none">
                        {animal.name || "Unnamed Animal"}
                    </h1>
                    <Chip 
                        size="sm" 
                        color={statusColor} 
                        variant="flat" 
                        startContent={statusIcon}
                        className="capitalize font-semibold"
                    >
                        {animal.approvalStatus}
                    </Chip>
                </div>
                <div className="flex items-center gap-2 text-xs text-default-500">
                    <span>ID: {animal.animalID}</span>
                    <span className="w-1 h-1 bg-default-300 rounded-full"></span>
                    <span>GN: {animal.gn}</span>
                </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Tooltip content="Copy Page Link">
                <Button isIconOnly variant="flat" size="sm" onPress={handleShare}>
                    <Share2 size={18} />
                </Button>
            </Tooltip>
            <Tooltip content="Print Certificate">
                <Button isIconOnly variant="flat" size="sm" onPress={handlePrint}>
                    <Printer size={18} />
                </Button>
            </Tooltip>
            <Button 
                color="primary" 
                size="sm" 
                className="font-medium bg-emerald-600 shadow-lg shadow-emerald-600/20"
                startContent={<CheckCircle2 size={16}/>}
            >
                Verified Genuine
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        
        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            
            {/* Left Column: Digital Dossier (The new data display) */}
            <div className="lg:col-span-5 flex flex-col gap-6 print:hidden order-2 lg:order-1">
                
                {/* Animal Profile Card */}
                <Card className="w-full shadow-md border border-default-100">
                    <CardHeader className="flex gap-4 p-6">
                        <div className="relative group">
                            <Image 
                                src={animal.image || "https://via.placeholder.com/150?text=No+Image"} 
                                alt={animal.name}
                                width={100}
                                height={100}
                                radius="lg"
                                className="object-cover border-2 border-default-200 shadow-sm"
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center text-white text-xs cursor-pointer">
                                View
                            </div>
                        </div>
                        <div className="flex flex-col justify-center gap-1 w-full">
                            <div className="flex justify-between items-start">
                                <h3 className="font-bold text-xl">{animal.name || "N/A"}</h3>
                                <Snippet symbol="" size="sm" variant="flat" className="text-tiny" codeString={animal.animalID}>ID</Snippet>
                            </div>
                            <p className="text-default-500 text-sm flex items-center gap-1">
                                <Dna size={14}/> {animal.evaluation || "UnAssigned Evaluation"}
                            </p>
                            <p className="text-default-500 text-sm flex items-center gap-1">
                                <CalendarDays size={14}/> Born: {formatDate(animal.birthDate)} ({calculateAge(animal.birthDate)})
                            </p>
                        </div>
                    </CardHeader>
                </Card>

                {/* Detailed Data Tabs */}
                <Card className="w-full flex-1 shadow-md border border-default-100">
                    <CardBody className="p-0">
                        <Tabs 
                            aria-label="Animal Data" 
                            variant="underlined"
                            color="primary"
                            classNames={{
                                tabList: "gap-6 w-full relative rounded-none p-0 border-b border-divider px-6",
                                cursor: "w-full bg-emerald-600",
                                tab: "max-w-fit px-0 h-12",
                                tabContent: "group-data-[selected=true]:text-emerald-600 font-medium"
                            }}
                            selectedKey={activeTab}
                            onSelectionChange={(key) => setActiveTab(key as string)}
                        >
                            <Tab key="identity" title="Identity & Physical">
                                <ScrollShadow className="h-[400px] w-full p-6">
                                    <div className="space-y-6">
                                        <SectionHeader title="Basic Information" icon={<Info size={16}/>} />
                                        <DataGrid>
                                            <DataItem label="Genetic Number (GN)" value={animal.gn} />
                                            <DataItem label="Registration (DGR)" value={animal.dgr} />
                                            <DataItem label="Sex" value={animal.sex} />
                                            <DataItem label="Breeding Type" value={animal.breedingType} />
                                            <DataItem label="Status" value={animal.animalStatus} />
                                            <DataItem label="Racial Comp." value={animal.racialComposition} />
                                        </DataGrid>

                                        <SectionHeader title="Physical Traits" icon={<Scale size={16}/>} />
                                        <DataGrid>
                                            <DataItem label="Coat Color" value={animal.coatColor} />
                                            <DataItem label="Size/Frame" value={animal.size} />
                                            <DataItem label="Birth Condition" value={animal.birthCondition} />
                                            <DataItem label="Birth Weight" value={`${animal.birthWeight || '--'} kg`} />
                                        </DataGrid>

                                        <SectionHeader title="Ownership" icon={<MapPin size={16}/>} />
                                        {/* Assuming farm/farmer are populated or just IDs */}
                                        <div className="p-3 bg-default-50 rounded-lg border border-default-100">
                                            <p className="text-tiny text-default-500 uppercase font-bold">Associated Farm</p>
                                            <p className="text-sm font-medium">{animal.farm ? "Registered Farm" : "N/A"}</p>
                                        </div>
                                    </div>
                                </ScrollShadow>
                            </Tab>
                            
                            <Tab key="metrics" title="Growth Metrics">
                                <ScrollShadow className="h-[400px] w-full p-6">
                                    <div className="space-y-6">
                                        <SectionHeader title="Weight Progression" icon={<Activity size={16}/>} />
                                        <div className="grid grid-cols-2 gap-3">
                                            <MetricCard label="Current Weight" value={animal.weight} unit="kg" />
                                            <MetricCard label="ADG" value={animal.adg} unit="kg/day" tooltip="Average Daily Gain" />
                                            <MetricCard label="Weaning (WW)" value={animal.ww} unit="kg" />
                                            <MetricCard label="Yearling (YW)" value={animal.yw} unit="kg" />
                                            <MetricCard label="120 Day (W120)" value={animal.w120} unit="kg" />
                                            <MetricCard label="Post Weaning (PW)" value={animal.pw} unit="kg" />
                                        </div>
                                        
                                        <SectionHeader title="Genetic Merits" icon={<Dna size={16}/>} />
                                        <DataGrid>
                                            <DataItem label="Merits 120d" value={animal.geneticMerits?.merits120} />
                                            <DataItem label="Merits 240d" value={animal.geneticMerits?.merits240} />
                                            <DataItem label="Merits 420d" value={animal.geneticMerits?.merits420} />
                                        </DataGrid>
                                    </div>
                                </ScrollShadow>
                            </Tab>

                            <Tab key="lineage" title="Lineage Summary">
                                <ScrollShadow className="h-[400px] w-full p-6">
                                    <div className="space-y-6">
                                        <div className="flex flex-col gap-4">
                                            <div className="border-l-4 border-blue-500 pl-4 py-1">
                                                <p className="text-tiny uppercase text-default-500 font-bold">Sire (Father)</p>
                                                <p className="font-semibold text-lg">{animal.sireName || "Unknown"}</p>
                                                <p className="text-xs text-default-400">Breed: {animal.sireBreed} | DGR: {animal.sireDGR}</p>
                                            </div>
                                            <div className="border-l-4 border-pink-500 pl-4 py-1">
                                                <p className="text-tiny uppercase text-default-500 font-bold">Dam (Mother)</p>
                                                <p className="font-semibold text-lg">{animal.damName || "Unknown"}</p>
                                                <p className="text-xs text-default-400">Breed: {animal.damBreed} | DGR: {animal.damDGR}</p>
                                            </div>
                                        </div>
                                        
                                        <Divider />
                                        <SectionHeader title="Grandparents" icon={<Dna size={16}/>} />
                                        <DataGrid cols={1}>
                                            <DataItem label="Paternal Grand Sire" value={animal.paternalGrandSireName} />
                                            <DataItem label="Maternal Grand Sire" value={animal.maternalGrandSireName} />
                                        </DataGrid>
                                    </div>
                                </ScrollShadow>
                            </Tab>
                        </Tabs>
                    </CardBody>
                </Card>
            </div>

            {/* Right Column: The Certificate Template */}
            <div className="lg:col-span-7 flex flex-col gap-4 order-1 lg:order-2">
                 <div className="bg-default-50 rounded-xl border border-default-200 p-4 lg:p-8 flex items-center justify-center overflow-hidden relative min-h-[600px]">
                    <div className="absolute top-4 right-4 z-10 print:hidden">
                        <Chip variant="shadow" classNames={{base: "bg-white/80 backdrop-blur"}} size="sm">Preview Mode</Chip>
                    </div>
                    {/* Centering Wrapper for the existing tool */}
                    <div className="scale-[0.65] sm:scale-[0.75] md:scale-[0.85] lg:scale-100 transition-transform origin-top md:origin-center">
                        <AdvancedGeneticTool animal={animal} />
                    </div>
                 </div>
                 
                 <div className="flex justify-between items-center text-xs text-default-400 px-2 print:hidden">
                    <p>Certificate UUID: {id}</p>
                    <p>Last Updated: {formatDate(animal.updatedAt)}</p>
                 </div>
            </div>

        </div>
      </div>
      
      {/* Footer */}
      <div className="text-center mt-12 text-default-400 text-xs py-8 border-t border-default-200 print:hidden">
        <div className="flex justify-center gap-4 mb-2">
            <Link to="#" className="hover:text-default-600 transition-colors">Privacy Policy</Link>
            <Link to="#" className="hover:text-default-600 transition-colors">Terms of Service</Link>
            <Link to="#" className="hover:text-default-600 transition-colors">Contact Registry</Link>
        </div>
        <p>© {new Date().getFullYear()} Sila Genetics. All rights reserved.</p>
        <p>Bovine Genetic Authentication System v1.0 • Addis Ababa, Ethiopia</p>
      </div>
    </div>
  );
};

// --- Sub-components for Cleaner Code ---

const SectionHeader = ({ title, icon }: { title: string, icon: React.ReactNode }) => (
    <div className="flex items-center gap-2 pb-2 border-b border-default-100">
        <span className="text-emerald-600 bg-emerald-50 p-1.5 rounded-md">{icon}</span>
        <h4 className="text-sm font-bold text-default-700 uppercase tracking-wide">{title}</h4>
    </div>
);

const DataGrid = ({ children, cols = 2 }: { children: React.ReactNode, cols?: number }) => (
    <div className={`grid grid-cols-${cols} gap-y-4 gap-x-2`}>
        {children}
    </div>
);

const DataItem = ({ label, value }: { label: string, value: any }) => (
    <div className="flex flex-col">
        <span className="text-[10px] uppercase text-default-400 font-semibold">{label}</span>
        <span className="text-sm font-medium text-default-800 break-words">
            {value === true ? "Yes" : value === false ? "No" : value || "—"}
        </span>
    </div>
);

const MetricCard = ({ label, value, unit, tooltip }: { label: string, value: any, unit: string, tooltip?: string }) => {
    const content = (
        <div className="bg-default-50 p-3 rounded-lg border border-default-100 flex flex-col items-center justify-center text-center">
            <span className="text-tiny text-default-500 uppercase font-bold mb-1">{label}</span>
            <span className="text-lg font-black text-emerald-700">
                {value ? value : "--"} <span className="text-xs font-normal text-default-400">{unit}</span>
            </span>
        </div>
    );

    if (tooltip) {
        return <Tooltip content={tooltip}><div className="cursor-help">{content}</div></Tooltip>;
    }
    return content;
};

export default AnimalVerificationPage;